import type { PipelineStepContext } from '../../../payment/payment-types';

const DIGITAL_WALLET_ERROR_CODES = new Set([
  'GOOGLE_PAY_CANCELLED',
  'GOOGLE_PAY_NO_DATA',
  'GOOGLE_PAY_REQUEST_FAILED',
  'GOOGLE_PAY_ERROR',
  'GOOGLE_PAY_API_NOT_LOADED',
  'GOOGLE_PAY_CONFIG_MISSING',
  'APPLE_PAY_ERROR',
  'APPLE_PAY_NOT_AVAILABLE',
  'APPLE_PAY_NO_CARDS',
  'APPLE_PAY_AVAILABILITY_CHECK_FAILED',
  'APPLE_PAY_CONFIG_MISSING',
  'APPLE_PAY_API_NOT_LOADED',
  'APPLE_PAY_NOT_SUPPORTED',
]);

const NON_RETRYABLE_PAYMENT_ERROR_CODES = new Set([
  'COMMIT_ORDER_COOLING_PERIOD',
  'COMMIT_ORDER_LOYALTY_VALIDATION',
  'PAYLOAD_BUILD_FAILED',
  'CONTEXT_NOT_READY',
]);

const COMMIT_FAILURE_ERROR_CODES = new Set(['COMMIT_ORDER_FAILED', 'COMMIT_ORDER_UNCONFIRMED']);

const COMMIT_FRESH_CONTEXT_ERROR_CODES = new Set([
  'MISSING_ENCRYPTION_RESULT',
  'COMMIT_ORDER_FAILED',
  'COMMIT_ORDER_UNCONFIRMED',
  'ENCRYPTION_FAILED',
]);

export function isDigitalWalletPaymentError(errorCode: string | undefined): boolean {
  return !!errorCode && DIGITAL_WALLET_ERROR_CODES.has(errorCode);
}

export function isRetryablePaymentError(errorCode: string | undefined): boolean {
  if (!errorCode || isDigitalWalletPaymentError(errorCode)) {
    return false;
  }

  return !NON_RETRYABLE_PAYMENT_ERROR_CODES.has(errorCode);
}

export function isCommitFailureRetry(errorCode: string | undefined): boolean {
  return !!errorCode && COMMIT_FAILURE_ERROR_CODES.has(errorCode);
}

export function getDefaultRetryStep(errorCode: string | undefined): string | undefined {
  switch (errorCode) {
    case 'COMMIT_ORDER_FAILED':
    case 'COMMIT_ORDER_UNCONFIRMED':
      return 'CommitOrderStep';
    case 'ENCRYPTION_FAILED':
    case 'MISSING_ENCRYPTION_RESULT':
      return 'EncryptionStep';
    case 'CART_EMPTY':
    case 'CART_VALIDATION_FAILED':
      return 'ValidateCartStep';
    default:
      return undefined;
  }
}

export function needsFreshPipelineContextOnRetry(errorCode: string | undefined): boolean {
  return !!errorCode && COMMIT_FRESH_CONTEXT_ERROR_CODES.has(errorCode);
}

/** Digital wallet tokens are single-use; always re-authorize before commit on retry. */
export function getDigitalWalletRetryStep(
  paymentMethod: string | null | undefined,
  retryFromStep: string | undefined
): string | undefined {
  if (!retryFromStep) {
    return retryFromStep;
  }

  if (paymentMethod === 'googlePay' && retryFromStep === 'CommitOrderStep') {
    return 'GooglePayStep';
  }

  if (paymentMethod === 'applePay' && retryFromStep === 'CommitOrderStep') {
    return 'ApplePayStep';
  }

  return retryFromStep;
}

/**
 * Drops the wallet authorization (and anything derived from it) so a retry cannot
 * commit with a spent token. The wallet step repopulates both on the next run.
 */
export function clearDigitalWalletDetailsFromContext(
  context: PipelineStepContext
): PipelineStepContext {
  if (context.paymentMethod === 'googlePay') {
    const { googlePayDetails: _googlePayDetails, encryptionResult: _encryptionResult, ...rest } =
      context;
    return { ...rest, creditType: null } as PipelineStepContext;
  }

  if (context.paymentMethod === 'applePay') {
    const { applePayDetails: _applePayDetails, encryptionResult: _encryptionResult, ...rest } =
      context;
    return { ...rest, creditType: null } as PipelineStepContext;
  }

  return context;
}
